/**
 * Execution bulk-search descriptor — adapts SearchExecutions to
 * BulkSearchResults. The queried ids go in as executionIds; status/state/type
 * are server-side PropertyFilter refinements (type via
 * additionalFilters.executionType).
 */
import Badge from '@cloudscape-design/components/badge';
import { PropertyFilterProps } from '@cloudscape-design/components/property-filter';
import { titleCase, fullTimestamp } from 'src/store/slices/paymentDetail/paymentDetailFormat';
import { formatLatency } from 'src/utils/formatters';
import { DetailLink } from 'src/components/common/DetailLink';
import {
  ApiExecutionSummary,
  ExecutionFilters,
  SearchExecutionsInput,
} from 'src/store/slices/searchExecutions/searchExecutionsApi';
import { searchExecutions, clearSearchExecutions } from 'src/store/slices/searchExecutions/searchExecutionsSlice';
import {
  BulkSearchArgs,
  BulkSearchDescriptor,
  StatusCell,
  dash,
  firstOf,
} from './bulkSearchShared';

const FILTERING_PROPERTIES: PropertyFilterProps.FilteringProperty[] = [
  { key: 'status', propertyLabel: 'Status', groupValuesLabel: 'Status values', operators: ['='] },
  { key: 'state', propertyLabel: 'State', groupValuesLabel: 'State values', operators: ['='] },
  { key: 'type', propertyLabel: 'Type', groupValuesLabel: 'Type values', operators: ['='] },
];

const buildInput = ({ ids, pageNumber, pageSize, filters }: BulkSearchArgs): SearchExecutionsInput => {
  const f: ExecutionFilters = { executionIds: ids };
  const status = firstOf(filters, 'status');
  const state = firstOf(filters, 'state');
  if (status) f.beaconExecutionStatus = status;
  if (state) f.beaconExecutionState = state;
  if (filters.type?.length) f.additionalFilters = { executionType: filters.type };
  return { filters: f, pagination: { pageNumber, pageSize } };
};

export const executionSearchDescriptor: BulkSearchDescriptor<ApiExecutionSummary> = {
  entity: 'execution',
  title: 'Bulk execution search',
  path: '/executions',
  filteringProperties: FILTERING_PROPERTIES,
  columns: [
    {
      id: 'id',
      header: 'Execution id',
      cell: (e) => <DetailLink to={`/execution?id=${encodeURIComponent(e.id)}`}>{e.id}</DetailLink>,
    },
    { id: 'executionType', header: 'Type', cell: (e) => (e.executionType ? <Badge>{e.executionType}</Badge> : '-') },
    { id: 'beaconStatus', header: 'Beacon status', cell: (e) => <StatusCell state={e.beaconExecutionState} label={e.beaconExecutionStatus} /> },
    { id: 'system', header: 'System', cell: (e) => dash(e.currentSystem) },
    { id: 'direction', header: 'Direction', cell: (e) => (e.currentDirection ? titleCase(e.currentDirection) : '-') },
    { id: 'artifactId', header: 'Artifact', cell: (e) => (e.artifactId ? <DetailLink to={`/artifact?id=${encodeURIComponent(e.artifactId)}`}>{e.artifactId}</DetailLink> : '-') },
    { id: 'payments', header: 'Payments', cell: (e) => e.paymentIds?.length ?? 0 },
    { id: 'totalLatency', header: 'Total latency', cell: (e) => formatLatency(e.totalLatencyMs) },
    { id: 'updatedAt', header: 'Updated', cell: (e) => (e.updatedAt ? fullTimestamp(e.updatedAt) : '-') },
  ],
  run: (dispatch, args) => { dispatch(searchExecutions(buildInput(args))); },
  clear: (dispatch) => { dispatch(clearSearchExecutions()); },
  select: (s) => ({
    items: s.searchExecutions.executions,
    total: s.searchExecutions.total,
    loading: s.searchExecutions.loading,
    error: s.searchExecutions.error,
    regionResults: s.searchExecutions.regionResults,
    searchedRegions: s.searchExecutions.searchedRegions,
    regionErrors: s.searchExecutions.regionErrors,
  }),
};
